import axios from 'axios';
import config from '../config/config';

// Müşteri portalı için ayrı axios instance
const customerPortalApi = axios.create({
  baseURL: `${config.backendUrl}/api/customer-portal`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token'ı localStorage'dan al
const getCustomerToken = () => {
  return localStorage.getItem('customer-portal-token') || null;
};

// Token'ın expire olup olmadığını kontrol et
const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp * 1000; // JWT exp saniye cinsinden, Date milisaniye cinsinden
    return Date.now() >= exp;
  } catch (error) {
    return true; // Parse hatası varsa token geçersiz sayılır
  }
};

// Token'ın expire olup olmadığını kontrol et (public function)
export const checkTokenExpiry = () => {
  const token = getCustomerToken();
  if (!token) return true;
  return isTokenExpired(token);
};

// Request interceptor - token ekle
customerPortalApi.interceptors.request.use(
  (config) => {
    const token = getCustomerToken();
    
    // Token varsa header'a ekle (expire kontrolü response interceptor'da yapılacak)
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - 401 hatası durumunda logout
customerPortalApi.interceptors.response.use(
  (response) => response,
  (error) => {
    // 401 hatası geldiğinde (token expire veya geçersiz)
    if (error.response?.status === 401) {
      // Token'ı temizle
      localStorage.removeItem('customer-portal-token');
      localStorage.removeItem('customer-portal-user');
      
      const isSubdomain = window.location.hostname === 'musteri.janjan.online';
      const isLoginPage = window.location.pathname.includes('/login');
      
      // Sadece login sayfasında değilsek yönlendir (sonsuz döngüyü önlemek için)
      if (!isLoginPage) {
        // Kısa bir gecikme ile yönlendir (toast mesajının görünmesi için)
        setTimeout(() => {
          window.location.href = isSubdomain ? '/login' : '/musteri-portal/login';
        }, 100);
      }
    }
    
    // Network hatası veya diğer hatalar için normal error handling
    return Promise.reject(error);
  }
);

// API fonksiyonları
export const customerPortalService = {
  // Giriş
  login: async (portalUsername, portalPassword) => {
    const response = await customerPortalApi.post('/login', {
      portalUsername,
      portalPassword
    });
    
    // Token ve kullanıcı bilgilerini kaydet
    if (response.data.success && response.data.data.token) {
      localStorage.setItem('customer-portal-token', response.data.data.token);
      localStorage.setItem('customer-portal-user', JSON.stringify(response.data.data.customer));
    }
    
    return response.data;
  },

  // Çıkış
  logout: () => {
    localStorage.removeItem('customer-portal-token');
    localStorage.removeItem('customer-portal-user');
  },

  // Müşteri bilgilerini getir
  getCustomerInfo: async () => {
    const response = await customerPortalApi.get('/me');
    return response.data;
  },

  // Dashboard özet istatistikleri
  getDashboardStats: async () => {
    const response = await customerPortalApi.get('/dashboard-stats');
    return response.data;
  },

  // Token kontrolü
  isAuthenticated: () => {
    const token = getCustomerToken();
    if (!token) return false;
    // Token expire kontrolü
    return !isTokenExpired(token);
  },

  // Kullanıcı bilgilerini getir
  getCustomer: () => {
    const userStr = localStorage.getItem('customer-portal-user');
    return userStr ? JSON.parse(userStr) : null;
  },

  // Siparişleri getir
  getOrders: async (status = null) => {
    const params = status ? { status } : {};
    const response = await customerPortalApi.get('/orders', { params });
    return response.data;
  },

  // Sipariş güncelle
  updateOrder: async (orderId, data) => {
    const response = await customerPortalApi.put(`/orders/${orderId}`, data);
    return response.data;
  },

  // Sipariş iptal et
  cancelOrder: async (orderId, note = '') => {
    const response = await customerPortalApi.post(`/orders/${orderId}/cancel`, { note });
    return response.data;
  },

  // Termin / teslimata hazır: iptal talebi oluştur (admin onayı gerekir)
  requestCancelOrder: async (orderId, reason = '') => {
    const response = await customerPortalApi.post(`/orders/${orderId}/cancel-request`, { reason });
    return response.data;
  },

  // Siparişi beklet
  holdOrder: async (orderId, note = '') => {
    const response = await customerPortalApi.post(`/orders/${orderId}/hold`, { note });
    return response.data;
  },

  // Siparişi devam ettir
  resumeOrder: async (orderId, note = '') => {
    const response = await customerPortalApi.post(`/orders/${orderId}/resume`, { note });
    return response.data;
  },

  // Generic GET request
  get: async (path, config = {}) => {
    const response = await customerPortalApi.get(path, config);
    return response;
  },

  // Generic POST request
  post: async (path, data = {}, config = {}) => {
    const response = await customerPortalApi.post(path, data, config);
    return response;
  },

  // Generic PUT request
  put: async (path, data = {}, config = {}) => {
    const response = await customerPortalApi.put(path, data, config);
    return response;
  },

  // Generic DELETE request
  delete: async (path, config = {}) => {
    const response = await customerPortalApi.delete(path, config);
    return response;
  },

  // Dijital Baskı API'leri
  getAktifSiparisler: async (search = '') => {
    const params = search ? { search } : {};
    const response = await customerPortalApi.get('/digital-printing/aktif-siparisler', { params });
    return response.data;
  },

  getTamamlananSiparisler: async (search = '') => {
    const params = search ? { search } : {};
    const response = await customerPortalApi.get('/digital-printing/tamamlanan-siparisler', { params });
    return response.data;
  },

  getNumuneler: async (search = '') => {
    const params = search ? { search } : {};
    const response = await customerPortalApi.get('/digital-printing/numuneler', { params });
    return response.data;
  },

  // Dijital baskı siparişi oluştur
  createDigitalPrintingOrder: async (payload) => {
    const response = await customerPortalApi.post('/digital-printing/orders', payload);
    return response.data;
  }
};

// Stock cards için API (müşteri portalı için)
export const getActiveStockCards = async () => {
  const response = await customerPortalApi.get('/stock-cards/active');
  return response.data;
};

export default customerPortalApi;
